import { Form, Formik } from 'formik';
import { Values } from './InvoiceSubscriberModalForm.types';
import * as Yup from 'yup';
import FormField from '../../components/field/FormField';
import Button from '../../components/button/Button';
import { useEffect, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { apiUrl } from '../../api/apiUrl';
import http from '../../api/axios';
import { useLoading } from '../../context/loadingContext';
import { handleApiErrors } from '../../api/apiErrorHandler';
import { useTranslation } from '../../context/translationContext';
import { Company } from '../../types/Company';
import SelectAutocomplete from '../../components/select/SelectAutocomplete';
import { useCurrentCompany } from '../../hooks/useCompany';
import { DocumentType, InvoiceBaseInputs } from '../../types/Invoice';

interface Props {
  initialData: Company | null;
  onSubmit: (values: Values) => void;
  onClose: () => void;
  isDeliveryAddress: boolean;
  setIsDeliveryAddress: React.Dispatch<React.SetStateAction<boolean>>;
  documentType: DocumentType;
}

/**
 * Form for adding subscriber to document
 */
export const InvoiceSubscriberForm = ({
  onSubmit,
  onClose,
  initialData,
  isDeliveryAddress,
  setIsDeliveryAddress,
  documentType,
}: Props) => {
  const { t } = useTranslation();
  const [data, setData] = useState<any | null>(null);
  const [initialValues, setInitialValues] = useState<any | null>(initialData);
  const { startLoading, stopLoading, loading } = useLoading();
  const [subscriberDeliveryHints, setSubscriberDeliveryHints] = useState<Company[]>([]);
  const [subscriberDeliveryName, setSubscriberDeliveryName] = useState<string>(
    initialData?.supplierDeliveryName ?? '',
  );
  const { company } = useCurrentCompany();

  /**
   * Loads data by companyId
   */
  const fetchDataByCompanyId = async (companyId: string) => {
    try {
      startLoading();
      const response = await http.apiGet({
        url: `${apiUrl.COMPANY_SEARCH_BY_ID}?companyId=${companyId}`,
      });
      setData(response?.data);
      stopLoading();
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  const _initialValues: Values = {
    name: '',
    description: '',
    subscriberAddressLine1: '',
    subscriberAddressLine2: '',
    subscriberAddressCity: '',
    subscriberAddressPostCode: '',
    subscriberAddressCountry: '',
    subscriberCompanyId: '',
    subscriberVatId: '',
    subscriberDeliveryName: '',
    subscriberDeliveryAddressLine1: '',
    subscriberDeliveryAddressLine2: '',
    subscriberDeliveryAddressCity: '',
    subscriberDeliveryAddressPostCode: '',
    subscriberDeliveryAddressCountry: '',
  };

  const companySchema = Yup.object().shape({
    name: Yup.string().required('Name is required.'),
  });

  /**
   * Search company in ARES
   */
  const handleSearchCompany = (companyId: string) => fetchDataByCompanyId(companyId);

  useEffect(() => {
    if (!data) return;
    const initialValues = {
      name: data.name ?? '',
      description: data.description ?? '',
      subscriberAddressLine1: data.supplierAddressLine1 ?? '',
      subscriberAddressLine2: data.supplierAddressLine2 ?? '',
      subscriberAddressCity: data.supplierAddressCity ?? '',
      subscriberAddressPostCode: data.supplierAddressPostCode ?? '',
      subscriberAddressCountry: data.supplierAddressCountry ?? '',
      subscriberCompanyId: data.supplierCompanyId ?? '',
      subscriberVatId: data.supplierVatId ?? '',
      subscriberDeliveryName: data.supplierDeliveryName ?? '',
      subscriberDeliveryAddressLine1: data.supplierDeliveryAddressLine1 ?? '',
      subscriberDeliveryAddressLine2: data.supplierDeliveryAddressLine2 ?? '',
      subscriberDeliveryAddressCity: data.supplierDeliveryAddressCity ?? '',
      subscriberDeliveryAddressPostCode: data.supplierDeliveryAddressPostCode ?? '',
      subscriberDeliveryAddressCountry: data.supplierDeliveryAddressCountry ?? '',
    };
    setSubscriberDeliveryName(data.supplierDeliveryName);
    setInitialValues(initialValues);
  }, [data]);

  useEffect(() => {
    if (!initialData) return setInitialValues(null);
    const initialValues = {
      name: initialData.name ?? '',
      description: initialData.description ?? '',
      subscriberAddressLine1: initialData.supplierAddressLine1 ?? '',
      subscriberAddressLine2: initialData.supplierAddressLine2 ?? '',
      subscriberAddressCity: initialData.supplierAddressCity ?? '',
      subscriberAddressPostCode: initialData.supplierAddressPostCode ?? '',
      subscriberAddressCountry: initialData.supplierAddressCountry ?? '',
      subscriberCompanyId: initialData.supplierCompanyId ?? '',
      subscriberVatId: initialData.supplierVatId ?? '',
      subscriberDeliveryName: initialData.supplierDeliveryName ?? '',
      subscriberDeliveryAddressLine1: initialData.supplierDeliveryAddressLine1 ?? '',
      subscriberDeliveryAddressLine2: initialData.supplierDeliveryAddressLine2 ?? '',
      subscriberDeliveryAddressCity: initialData.supplierDeliveryAddressCity ?? '',
      subscriberDeliveryAddressPostCode: initialData.supplierDeliveryAddressPostCode ?? '',
      subscriberDeliveryAddressCountry: initialData.supplierDeliveryAddressCountry ?? '',
    };
    setInitialValues(initialValues);
    setSubscriberDeliveryName(initialData.supplierDeliveryName ?? '');
  }, [initialData]);

  /**
   * Loads hints for previous used delivery subsciber of current selected subscriber
   */
  const fetchSubscriberDeliveryHintsDataData = async (query: string, supplierName: string) => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.ACCOUNTING_DOCUMENT_SUBSCRIBER_DELIVERY_HINT}?companyId=${
        company.id
      }&documentType=${documentType}${
        query ? `&search=` + query : ``
      }&supplierName=${supplierName}`,
    });
    let hints: any[] = [];
    if (response?.data)
      response?.data.map(
        (item: any, index: number) => (hints = [...hints, { ...item, tmpId: index }]),
      );
    setSubscriberDeliveryHints(hints);
  };

  /**
   * Set search query for subscriber delivery hints
   * @param query
   */
  const handleChangeSubscriberNameQuery = (query: string, values: Values) => {
    if (!values.name) return;
    fetchSubscriberDeliveryHintsDataData(query, values.name);
  };

  /**
   * Select subscriber delivery by autofill from input
   */
  const handleSelectSubscriberDelivery = (
    data: InvoiceBaseInputs,
    setFieldValue: (col: string, val: string) => void,
  ) => {
    // set form fields
    setSubscriberDeliveryName(data.subscriberDeliveryName ?? '');
    setFieldValue('subscriberDeliveryName', data.subscriberDeliveryName ?? '');
    setFieldValue('subscriberDeliveryAddressLine1', data.subscriberDeliveryAddressLine1 ?? '');
    setFieldValue('subscriberDeliveryAddressLine2', data.subscriberDeliveryAddressLine2 ?? '');
    setFieldValue('subscriberDeliveryAddressCity', data.subscriberDeliveryAddressCity ?? '');
    setFieldValue(
      'subscriberDeliveryAddressPostCode',
      data.subscriberDeliveryAddressPostCode ?? '',
    );
    setFieldValue('subscriberDeliveryAddressCountry', data.subscriberDeliveryAddressCountry ?? '');
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || _initialValues}
      onSubmit={(e) => onSubmit({ ...e, subscriberDeliveryName: subscriberDeliveryName })}
      validationSchema={companySchema}
    >
      {({ errors, touched, resetForm, values, setFieldValue }) => (
        <>
          <Form className="w-full">
            <div className="flex flex-row">
              <div className="w-2/5">
                <div className="my-3">
                  <FormField
                    id="name"
                    name="name"
                    label={t('page.invoice.add-subscriber.input-name')}
                    errors={errors}
                    touched={touched}
                    disabled={loading}
                  />
                </div>
                <div className="my-3 w-full">
                  <div className="flex">
                    <div className="w-full">
                      <FormField
                        id="subscriberCompanyId"
                        name="subscriberCompanyId"
                        label={t('page.invoice.add-subscriber.input-company-id')}
                        disabled={loading}
                      />
                    </div>
                    <div className="ml-1">
                      <Button
                        onClick={(e: any) => {
                          e.preventDefault();
                          handleSearchCompany(values.subscriberCompanyId);
                        }}
                        disabled={loading}
                        className="!px-1.5"
                        icon={<MagnifyingGlassIcon className="h-5 w-5 text-lg	 cursor-pointer" />}
                      />
                    </div>
                  </div>
                </div>
                <div className="my-3">
                  <FormField
                    id="subscriberVatId"
                    name="subscriberVatId"
                    label={t('page.invoice.add-subscriber.input-vat-id')}
                    disabled={loading}
                  />
                </div>
                <div className="my-3">
                  <FormField
                    id="subscriberAddressCountry"
                    name="subscriberAddressCountry"
                    label={t('page.invoice.add-subscriber.input-address-country')}
                    disabled={loading}
                  />
                </div>
                <div className="my-3 flex flex-row items-center">
                  <label htmlFor={'isActive'} className="mr-5">
                    {t('page.invoice.add-subscriber.different-delivery-address')}
                  </label>
                  <FormField
                    name={'isDeliveryAddress'}
                    id={'isDeliveryAddress'}
                    type="checkbox"
                    onChange={(e) => {
                      // reset fields if unchecked
                      if (e && !e.target.checkded) {
                        setFieldValue('subscriberDeliveryName', '');
                        setFieldValue('subscriberDeliveryAddressPostCode', '');
                        setFieldValue('subscriberDeliveryAddressLine1', '');
                        setFieldValue('subscriberDeliveryAddressLine2', '');
                        setFieldValue('subscriberDeliveryAddressCity', '');
                        setFieldValue('subscriberDeliveryAddressCountry', '');
                        setSubscriberDeliveryName('');
                      }
                      setIsDeliveryAddress(e.target.checked);
                    }}
                    checked={isDeliveryAddress}
                    className={`w-4 h-4`}
                  />
                </div>
              </div>
              <div className="ml-5 w-3/5">
                <div className="my-3">
                  <FormField
                    id="subscriberAddressLine1"
                    name="subscriberAddressLine1"
                    label={t('page.invoice.add-subscriber.input-address-line-1')}
                    disabled={loading}
                  />
                </div>
                <div className="my-3">
                  <FormField
                    id="subscriberAddressLine2"
                    name="subscriberAddressLine2"
                    label={t('page.invoice.add-subscriber.input-address-line-2')}
                    disabled={loading}
                  />
                </div>
                <div className="my-3">
                  <FormField
                    id="subscriberAddressCity"
                    name="subscriberAddressCity"
                    label={t('page.invoice.add-subscriber.input-address-city')}
                    disabled={loading}
                  />
                </div>
                <div className="my-3">
                  <FormField
                    id="subscriberAddressPostCode"
                    name="subscriberAddressPostCode"
                    label={t('page.invoice.add-subscriber.input-address-zip')}
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
            {isDeliveryAddress && (
              <div className="animate">
                <p className="text-md font-medium leading-6 text-gray-900" id="modal-title">
                  {t('page.invoice.delivery-address')}
                </p>
                <div className="flex flex-row">
                  <div className="w-2/5">
                    <div className="my-3">
                      <SelectAutocomplete
                        name="subscriber-delivery-ac"
                        data={subscriberDeliveryHints ? subscriberDeliveryHints : []}
                        label={t('page.invoice.add-subscriber.input-name')}
                        labelKey="subscriberDeliveryName"
                        valueKey="subscriberDeliveryName"
                        displayPattern={'subscriberDeliveryName-subscriberDeliveryAddressLine1'}
                        placeholder={t('page.invoice.enter-subscriber-delivery-name')}
                        onSelected={(q) => handleSelectSubscriberDelivery(q, setFieldValue)}
                        value={subscriberDeliveryName ?? undefined}
                        onChange={(e) => {
                          if (typeof e !== 'string') return;
                          setSubscriberDeliveryName(e);
                        }}
                        onQueryChange={async (e) => {
                          if (typeof e !== 'string') return;
                          await handleChangeSubscriberNameQuery(e, values);
                        }}
                        onFocus={async () => {
                          setSubscriberDeliveryName('');
                          handleChangeSubscriberNameQuery('', values);
                        }}
                        resize={false}
                        className={`!py-2`}
                        searchWhileEmpty
                        isFormField
                      />
                    </div>
                    <div className="my-3">
                      <FormField
                        id="subscriberDeliveryAddressCountry"
                        name="subscriberDeliveryAddressCountry"
                        label={t('page.invoice.add-subscriber.input-address-country')}
                        disabled={loading}
                      />
                    </div>
                  </div>
                  <div className="ml-5 w-3/5">
                    <div className="my-3">
                      <FormField
                        id="subscriberDeliveryAddressLine1"
                        name="subscriberDeliveryAddressLine1"
                        label={t('page.invoice.add-subscriber.input-address-line-1')}
                        disabled={loading}
                      />
                    </div>
                    <div className="my-3">
                      <FormField
                        id="subscriberDeliveryAddressLine2"
                        name="subscriberDeliveryAddressLine2"
                        label={t('page.invoice.add-subscriber.input-address-line-2')}
                        disabled={loading}
                      />
                    </div>
                    <div className="my-3">
                      <FormField
                        id="subscriberDeliveryAddressCity"
                        name="subscriberDeliveryAddressCity"
                        label={t('page.invoice.add-subscriber.input-address-city')}
                        disabled={loading}
                      />
                    </div>
                    <div className="my-3">
                      <FormField
                        id="subscriberDeliveryAddressPostCode"
                        name="subscriberDeliveryAddressPostCode"
                        label={t('page.invoice.add-subscriber.input-address-zip')}
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div>
              <Button
                title={t('general.confirm')}
                type="submit"
                color="green"
                disabled={loading}
                className="mr-3 pl-10 pr-10"
              />
              <Button
                title={t('general.close')}
                color="gray"
                type="button"
                onClick={() => {
                  resetForm();
                  onClose();
                }}
                disabled={loading}
                className="mr-3 pl-10 pr-10"
              />
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};
